import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n";
import { hasDecision, saveConsent, readConsent } from "@/lib/consent";
import { X } from "lucide-react";

export default function CookieBanner() {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(true);

  useEffect(() => {
    if (!hasDecision()) {
      // Small delay so the banner doesn't fight with the hero animation
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    } else {
      const c = readConsent();
      if (c) setAnalytics(!!c.analytics);
    }
  }, []);

  useEffect(() => {
    // Listen for external requests to reopen the banner (e.g. from Cookie Policy page)
    const handler = () => {
      const c = readConsent();
      if (c) setAnalytics(!!c.analytics);
      setShowDetails(true);
      setVisible(true);
    };
    window.addEventListener("ecv:openCookieSettings", handler);
    return () => window.removeEventListener("ecv:openCookieSettings", handler);
  }, []);

  const acceptAll = () => {
    saveConsent({ analytics: true });
    setVisible(false);
    setShowDetails(false);
  };
  const rejectAll = () => {
    saveConsent({ analytics: false });
    setVisible(false);
    setShowDetails(false);
  };
  const savePrefs = () => {
    saveConsent({ analytics });
    setVisible(false);
    setShowDetails(false);
  };

  if (!visible) return null;

  const c = t.cookies;

  return (
    <>
      {/* Backdrop only when the details modal is open */}
      {showDetails && (
        <div
          className="fixed inset-0 z-[120] bg-black/40 backdrop-blur-sm"
          onClick={() => setShowDetails(false)}
          data-testid="cookie-modal-backdrop"
        />
      )}

      {/* Compact banner */}
      {!showDetails && (
        <div
          className="fixed inset-x-0 bottom-0 z-[110] px-4 pb-4 sm:px-6 sm:pb-6 pointer-events-none"
          data-testid="cookie-banner"
        >
          <div className="pointer-events-auto max-w-5xl mx-auto bg-[#14110D] text-[#FAF9F6] rounded-lg shadow-2xl border border-[#FAF9F6]/10 overflow-hidden">
            <div className="p-5 sm:p-6 grid gap-4 md:grid-cols-[1fr_auto] md:items-center">
              <div>
                <p className="font-serif-display text-xl sm:text-2xl">{c.bannerTitle}</p>
                <p className="mt-2 text-sm text-[#FAF9F6]/70 leading-relaxed">
                  {c.bannerBody}{" "}
                  <Link
                    to="/cookie-policy"
                    className="underline hover:text-[#C85A3C] transition"
                    data-testid="cookie-banner-policy-link"
                  >
                    {c.readPolicy}
                  </Link>
                </p>
              </div>
              <div className="flex flex-col sm:flex-row md:flex-col gap-2 min-w-[180px]">
                <button
                  onClick={acceptAll}
                  data-testid="cookie-accept-all"
                  className="px-5 py-2.5 bg-[#C85A3C] text-white text-sm font-medium tracking-wide hover:bg-[#B24A2E] transition rounded-sm"
                >
                  {c.acceptAll}
                </button>
                <button
                  onClick={rejectAll}
                  data-testid="cookie-reject-all"
                  className="px-5 py-2.5 border border-[#FAF9F6]/25 text-sm font-medium tracking-wide hover:border-[#FAF9F6]/60 transition rounded-sm"
                >
                  {c.rejectAll}
                </button>
                <button
                  onClick={() => setShowDetails(true)}
                  data-testid="cookie-customize"
                  className="px-5 py-2.5 text-sm font-medium text-[#FAF9F6]/70 hover:text-[#C85A3C] transition"
                >
                  {c.customize}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details / preferences modal */}
      {showDetails && (
        <div
          className="fixed inset-0 z-[130] flex items-center justify-center p-4"
          data-testid="cookie-preferences-modal"
        >
          <div className="bg-[#FAF9F6] text-[#2C2520] max-w-lg w-full max-h-[85vh] overflow-y-auto rounded-lg shadow-2xl relative">
            <button
              onClick={() => setShowDetails(false)}
              className="absolute top-3 right-3 p-2 text-[#2C2520]/50 hover:text-[#C85A3C] transition"
              aria-label="Close"
              data-testid="cookie-modal-close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="p-6 sm:p-8">
              <p className="overline text-[#C85A3C]">{c.prefsOverline}</p>
              <h3 className="font-serif-display text-2xl sm:text-3xl mt-2">{c.prefsTitle}</h3>
              <p className="mt-3 text-sm text-[#2C2520]/70 leading-relaxed">{c.prefsSub}</p>

              <div className="mt-6 space-y-4">
                <div className="border border-[#2C2520]/10 rounded-sm p-4 bg-white">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium">{c.necessaryTitle}</p>
                      <p className="text-xs text-[#2C2520]/60 mt-1">{c.alwaysActive}</p>
                    </div>
                    <span className="text-xs uppercase tracking-wider bg-[#2C2520]/10 px-2 py-1 rounded-sm">
                      {c.alwaysActive}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-[#2C2520]/70 leading-relaxed">{c.necessaryDesc}</p>
                </div>

                <div className="border border-[#2C2520]/10 rounded-sm p-4 bg-white">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-medium">{c.analyticsTitle}</p>
                    <label className="relative inline-flex items-center cursor-pointer" data-testid="cookie-toggle-analytics">
                      <input
                        type="checkbox"
                        checked={analytics}
                        onChange={(e) => setAnalytics(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-[#2C2520]/20 peer-checked:bg-[#C85A3C] transition rounded-full relative">
                        <div className={`absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full transition ${analytics ? "translate-x-5" : ""}`}></div>
                      </div>
                    </label>
                  </div>
                  <p className="mt-2 text-sm text-[#2C2520]/70 leading-relaxed">{c.analyticsDesc}</p>
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-2">
                <button
                  onClick={savePrefs}
                  data-testid="cookie-save-prefs"
                  className="px-5 py-2.5 bg-[#2C2520] text-white text-sm font-medium tracking-wide hover:bg-[#14110D] transition rounded-sm flex-1"
                >
                  {c.savePrefs}
                </button>
                <button
                  onClick={acceptAll}
                  data-testid="cookie-accept-all-modal"
                  className="px-5 py-2.5 bg-[#C85A3C] text-white text-sm font-medium tracking-wide hover:bg-[#B24A2E] transition rounded-sm flex-1"
                >
                  {c.acceptAll}
                </button>
              </div>
              <div className="mt-4 text-xs text-center">
                <Link to="/cookie-policy" className="underline text-[#2C2520]/60 hover:text-[#C85A3C]" data-testid="cookie-modal-policy-link">
                  {c.readPolicy}
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
